import axios from 'axios'
import config from 'config'
import authHeader from 'srcReact/helpers/auth-header'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import {
  doRequestAxios,
  doRequestAxiosAll,
  doRequestAxiosAllWithDiffParams,
  doRequestPlainGet
} from 'srcReact/helpers/apiHelper'
import { getCurrentLanguageFromCookie } from 'srcReact/helpers/utils'
import { isUserAuthorized } from 'srcReact/helpers/userHelper'
import { getCurrentApiDomain, getCurrentOrgId } from 'srcReact/helpers/organizationHelper'

export const generalService = {
  getTimezoneByLatLong,
  filesCreation,
  filesCreationWithFileId,
  filesCreationWithTag,
  searchInterest,
  suggestInterest,
  getCurrencyList,
  getLanguages,
  getAllGroupsByOrgId,
  getPopupCookie,
  setPopupCookie,
  getUserLocation,
  getAppStoreVersions,
  getUserIPAddress,
  addUserPageView,
  getTagUsers,
  postEventToCalendar,
  addToCalendarWithoutConnection,
  getMembersToTransferGroup,
  translateMessage,
  updateGDPR,
  getGDPR,
  getTimezones
}

let cancel

function getTimezoneByLatLong(latitude, longitude, timestamp) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader()
  }
  return doRequestAxios(
    `/location/time-zone?latitude=${latitude}&longitude=${longitude}&timestamp=${timestamp}`,
    requestOptions
  )
}
function filesCreationWithFileId(fileId) {
  const requestOptions = {
    method: 'POST',
    headers: authHeader()
  }
  return doRequestAxios(`/v2/file/duplication?file_id=${fileId}`, requestOptions)
}

function filesCreationWithTag(files, tag, is_logged, type, startingWithCount = 1) {
  const orgId = getCurrentOrgId()
  const language = getCurrentLanguageFromCookie()
  const requestOptions = {
    headers: authHeader(is_logged, '')
  }
  const domain = getCurrentApiDomain()
  const domainUrl = domain && domain.length > 0 ? `https://${domain}/api` : config.apiUrl
  const apiUrl = `${domainUrl}/v2/files/creation/${is_logged}?type=${type}&oid=${orgId}&language=${language}`
  const requestUrls = []
  for (let i = 0; i < files.length; i++) {
    const formData = new FormData()
    formData.append('file_1', files[i])
    formData.append('files_count', 1)
    //if there are 2 images and 2 documents in the post, then document tag index would be after image count
    // image tag and document tag cannot be same
    formData.append('message_tag', `${tag}-${i + startingWithCount}`)
    requestUrls.push(axios.post(apiUrl, formData, requestOptions))
  }
  return doRequestAxiosAllWithDiffParams(requestUrls, appConstantSettings.RETRY_COUNT)
}

function filesCreation(files, params, messageTag) {
  const language = getCurrentLanguageFromCookie()
  const formData = new FormData()
  formData.append('files_count', files.length)
  formData.append('language', language)
  if (params.organization_id && params.organization_id > 0) {
    formData.append('organization_id', params.organization_id)
  }
  if (messageTag) {
    formData.append('message_tag', messageTag)
  }

  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    if (file?.fileId) {
      // if image
      formData.append(`file_${i + 1}`, '')
      formData.append(`ik_id_${i + 1}`, file.fileId)
    } else {
      //if document
      formData.append(`file_${i + 1}`, files[i])
    }
  }

  const is_logged = params.is_logged || 0
  const requestOptions = {
    method: 'POST',
    headers: authHeader(is_logged, ''),
    params: formData
  }
  return doRequestAxios(
    `/v2/files/creation/${is_logged}?type=${params.type}`,
    requestOptions,
    appConstantSettings.RETRY_COUNT,
    params.apiSubDomain
  )
}

function searchInterest(interestType, search, offset, interestIds, isLogged) {
  const language = getCurrentLanguageFromCookie()
  const is_logged = isLogged || 0
  const nextOffset = offset ? `&offset=${offset}` : ''
  const quantity = quantity ? `&quantity=${quantity}` : `&quantity=50`
  const intstIds = interestIds ? `&interest_ids=${interestIds}` : ''
  const lang = `&language=${language}`
  const requestOptions = {
    method: 'GET',
    headers: authHeader(is_logged),
    params: {
      type: interestType
    }
  }
  return doRequestAxios(
    `/v2/interests/list/${is_logged}?search=${search}${nextOffset}${quantity}${intstIds}${lang}`,
    requestOptions
  )
}

function suggestInterest(type, name, origin, orgId, userEmail, entityId, is_logged = 0) {
  const language = getCurrentLanguageFromCookie()
  const lang = `&language=${language}`
  const params = { origin, type }
  if (
    origin === 'profile' ||
    origin === 'group_creation' ||
    origin === 'mixr_creation' ||
    origin === 'group_edit' ||
    origin === 'mixr_edit'
  ) {
    params.id_organization = orgId
  }
  if (origin === 'registration') {
    params.user_email = userEmail
  }
  if (origin === 'profile') {
    params.id_user = entityId
  }
  if (origin === 'group_edit') {
    params.id_group = entityId
  }
  if (origin === 'mixr_edit') {
    params.id_mixr = entityId
  }
  const requestOptions = {
    method: 'POST',
    headers: authHeader(is_logged),
    params: params
  }
  return doRequestAxios(`/v2/interests/suggestion/${is_logged}?name=${name}${lang}`, requestOptions)
}

function addUserPageView(page, id_object) {
  const requestOptions = {
    method: 'POST',
    headers: authHeader(),
    params: { page, id_object }
  }
  return doRequestAxios(`/v2/user/pageview/add`, requestOptions)
}

function getCurrencyList(is_logged) {
  const language = getCurrentLanguageFromCookie()
  const lang = `?language=${language}`
  const requestOptions = {
    method: 'GET',
    headers: authHeader(is_logged)
  }
  return doRequestAxios(`/v2/currencies/list/${is_logged}${lang}`, requestOptions)
}
function getLanguages(is_logged, env = 'all') {
  const language = getCurrentLanguageFromCookie()
  const environment = `?environment=${env}`
  const lang = `&language=${language}`

  const requestOptions = {
    method: 'GET',
    headers: authHeader(is_logged)
  }
  return doRequestAxios(`/v2/languages/list/${is_logged}${environment}${lang}`, requestOptions)
}

function getAllGroupsByOrgId(orgId, scope) {
  const scopeParam = scope.length > 0 ? `&scope=${scope}` : ''
  const requestOptions = {
    method: 'GET',
    headers: authHeader()
  }
  return doRequestAxios(`/v2/user/groups/list?organization_id=${orgId}${scopeParam}`, requestOptions)
}

function getPopupCookie(name) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader()
  }
  return doRequestAxios(`/v2/user/popup/check-display?popup=${name}`, requestOptions)
}

function setPopupCookie(name) {
  const requestOptions = {
    method: 'POST',
    headers: authHeader(),
    params: { popup: name }
  }
  return doRequestAxios(`/v2/user/popup/set-display`, requestOptions)
}

function getUserLocation() {
  const requestOptions = {
    method: 'GET',
    headers: authHeader()
  }
  return doRequestAxios(`/v2/user/chosen-location/get`, requestOptions)
}

function getUserIPAddress() {
  return doRequestPlainGet(appConstantSettings.USER_LOCATION_FINDER)
}

function getAppStoreVersions() {
  return doRequestPlainGet(`${config.apiUrl}/v2/mobile/store-app-versions`)
}

function getTagUsers(type, id, search, offset, quantity) {
  if (cancel !== undefined) {
    cancel()
  }

  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    cancelToken: new axios.CancelToken((c) => (cancel = c)),
    params: { type, id, search, offset, quantity }
  }

  return doRequestAxios(`/v2/tag/users-list`, requestOptions)
}

function postEventToCalendar(eventId, type, idExternalApiAuth, recurrenceUpdate) {
  const params = {
    event_id: eventId,
    id_external_api_auth: idExternalApiAuth,
    recurrence_update: recurrenceUpdate
  }
  const requestOptions = {
    method: 'POST',
    headers: authHeader(),
    params: params
  }
  let url = ''
  if (type == 'google') {
    url = '/v2/addtocalendar-google-api'
  } else if (type === 'microsoft') {
    url = '/v2/addtocalendar-microsoft-api'
  }
  return doRequestAxios(url, requestOptions)
}

function addToCalendarWithoutConnection(mixrId, type, mixrRecurrenceRule) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader()
  }
  return doRequestAxios(
    `/v2/mixr/addtocalendar?mixr_id=${mixrId}&type=${type}&recurrence_rule=${mixrRecurrenceRule}`,
    requestOptions
  )
  // return axios
  //   .get(`${config.apiUrl}/v2/mixr/addtocalendar?mixr_id=${mixrId}&type=${type}&recurrence_rule=${mixrRecurrenceRule}`, requestOptions)
  //   .then((response) => {
  //     if (response) {
  //       const { data } = response
  //       if (response.status === 200) {
  //         return data
  //       }
  //       if (!response.success || response.success == false) {
  //         if (response.status == 500) {
  //           redirectInternalError(history, response)
  //         } else if (response.status === 401) {
  //           userService.logout(true)
  //         } else {
  //           const error = (response && response.message) || response.statusText
  //           return Promise.reject(error)
  //         }
  //       }
  //     }
  //     return null
  //   })
  //   .catch(handleAxiosError)
}

function getMembersToTransferGroup(groupId, role, offset, quantity) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader()
  }
  let urlmember = `/v2/user/group-transfer?id_group=${groupId}&role=member&member_offset=${offset}&member_quantity=${quantity}`
  let urlCoOrg = ''
  if (offset === 0) {
    urlCoOrg = `/v2/user/group-transfer?id_group=${groupId}&role=co-organiser`
  }
  return doRequestAxiosAll([urlCoOrg, urlmember], requestOptions)
}

function translateMessage(organizationId, messageId, originalText, langaugeTarget) {
  const requestOptions = {
    method: 'POST',
    headers: authHeader(),
    params: {
      organization_id: organizationId,
      message_id: messageId,
      original_text: originalText,
      language_target: langaugeTarget
    }
  }
  return doRequestAxios(`/v2/translate`, requestOptions)
}

function updateGDPR(performace, functional, marketing) {
  const requestOptions = {
    method: 'PUT',
    headers: authHeader(),
    params: {
      with_performance_cookies: performace,
      with_functional_cookies: functional,
      with_marketing_cookies: marketing
    }
  }
  return doRequestAxios(`/v2/gdpr/authenticate/update`, requestOptions)
}

function getGDPR() {
  const requestOptions = {
    method: 'GET',
    headers: authHeader()
  }
  return doRequestAxios(`/v2/gdpr/authenticate/get`, requestOptions)
}

function getTimezones() {
  const is_logged = isUserAuthorized() ? 1 : 0
  const language = getCurrentLanguageFromCookie()
  const requestOptions = {
    method: 'GET',
    headers: authHeader()
  }
  return doRequestAxios(`/v2/time-zones/list/${is_logged}?language=${language}`, requestOptions)
}
